import React, { useCallback, useEffect } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { type DropResult, DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { graphql, usePreloadedQuery } from 'react-relay';
import AddIcon from '@atlaskit/icon/core/migration/add';
import { Section } from '@atlaskit/side-navigation';
import { Box, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useKbCreateCategoryActions } from '@atlassian/jira-kb-create-category-controller/src/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import useIsAdminHubAIEnabled from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCategoriesData } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/store/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import {
	useSuggestedTopics,
	useIsTopicsFiltered,
} from '@atlassian/jira-servicedesk-knowledge-performance/src/controllers/store/index.tsx';
import type { mainSideNavKBTypeCheckerQuery } from '@atlassian/jira-relay/src/__generated__/mainSideNavKBTypeCheckerQuery.graphql.ts';
import { messages } from '../messages.tsx';
import flagMessages from './messages.tsx';
import type { KnowledgeHubMenuProps } from './types.tsx';

export const KnowledgeHubMenu = ({ basePath, queryReference }: KnowledgeHubMenuProps) => {
	const { formatMessage } = useIntl();
	const [{ data: suggestedTopics, error, loading }, { fetchSuggestedTopics }] =
		useSuggestedTopics();
	const [isTopicsFiltered] = useIsTopicsFiltered();
	const checkIsTopicsFiltered = fg('suggested_topics_eap') ? isTopicsFiltered : true;
	const { showFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ data: pageContextData }] = usePageContext();
	const { projectContext, tenantContext } = pageContextData || {};
	const { projectId } = projectContext || {};
	const analyticsEvent = createAnalyticsEvent({});
	const activationId = tenantContext?.activationId || '';
	const [
		{ categories, isCategoriesSupported },
		{ reorderCategories: onCategoryReorder, fetchCategories },
	] = useCategoriesData();

	useEffect(() => {
		projectId && fetchCategories(projectId, createAnalyticsEvent({}), activationId);
	}, [projectId, fetchCategories, createAnalyticsEvent, activationId]);

	const [, { setCreateCategoryDisplay: openCreateCategory }] = useKbCreateCategoryActions();
	const { data: isAdminHubAIEnabled, fetchAIEnablement } =
		useIsAdminHubAIEnabled('jira-servicedesk');
	const showSuggestedTopics = fg('suggested_topics_eap')
		? !loading && !error && isAdminHubAIEnabled
		: fg('jsm-suggested-articles-in-kb');

	useEffect(() => {
		fg('suggested_topics_eap') && fetchAIEnablement();
	}, [fetchAIEnablement]);

	useEffect(() => {
		projectId &&
			fg('jsm-suggested-articles-in-kb') &&
			fetchSuggestedTopics(projectId, createAnalyticsEvent({}), activationId);
	}, [activationId, createAnalyticsEvent, fetchSuggestedTopics, projectId]);

	const knowledgeBaseSourceType = usePreloadedQuery<mainSideNavKBTypeCheckerQuery>(
		graphql`
			query mainSideNavKBTypeCheckerQuery($container: ID!, $cloudId: ID!, $isEnabled: Boolean!) {
				knowledgeBase(cloudId: $cloudId) @include(if: $isEnabled) {
					knowledgeBase(container: $container) {
						__typename
						... on KnowledgeBaseSources {
							edge @required(action: THROW) {
								node {
									sourceType @required(action: THROW)
								}
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	const sourceType =
		knowledgeBaseSourceType?.knowledgeBase?.knowledgeBase?.__typename === 'KnowledgeBaseSources'
			? knowledgeBaseSourceType.knowledgeBase.knowledgeBase.edge[0]?.node?.sourceType
			: null;

	const handleDragEnd = useCallback(
		(data: DropResult) => {
			const onReorderFailure = () => {
				showFlag({
					type: 'error',
					isAutoDismiss: true,
					title: formatMessage(flagMessages.reorderFailureTitle),
					description: formatMessage(flagMessages.reorderFailureDescription),
					messageId:
						'navigation-apps-sidebar-servicedesk.common.ui.knowledge-item.hub-menu.show-flag.error',
					messageType: 'transactional',
				});
			};
			const onReorderMismatch = () => {
				showFlag({
					type: 'success',
					isAutoDismiss: true,
					title: formatMessage(flagMessages.reorderMismatchTitle),
					description: flagMessages.reorderMismatchDescription.defaultMessage,
					messageId:
						'navigation-apps-sidebar-servicedesk.common.ui.knowledge-item.hub-menu.show-flag.success',
					messageType: 'transactional',
				});
			};

			if (data && data.source && data.destination) {
				if (projectId) {
					onCategoryReorder(
						projectId,
						data.source.index,
						data.destination.index,
						analyticsEvent,
						activationId,
						onReorderMismatch,
						onReorderFailure,
					);
				}
			}
		},
		[showFlag, formatMessage, projectId, onCategoryReorder, analyticsEvent, activationId],
	);

	const renderArticles = useCallback(() => {
		return (
			<MenuItem
				analytics={{ itemId: 'articles' }}
				href={`${basePath}/articles`}
				selectedOn={['knowledge-hub-browse-all-articles']}
			>
				{formatMessage(messages.allArticles)}
			</MenuItem>
		);
	}, [basePath, formatMessage]);

	const renderDrafts = useCallback(() => {
		return (
			<MenuItem
				analytics={{ itemId: 'drafts' }}
				href={`${basePath}/drafts`}
				selectedOn={['knowledge-hub-drafts']}
			>
				{formatMessage(messages.drafts)}
			</MenuItem>
		);
	}, [basePath, formatMessage]);

	const renderSuggestedTopics = useCallback(() => {
		return (
			<MenuItem
				analytics={{ itemId: 'suggested topics' }}
				href={`${basePath}/suggested-topics`}
				selectedOn={['knowledge-hub-suggested-topics']}
			>
				{formatMessage(messages.suggestedTopics)}
				{checkIsTopicsFiltered && !error && !loading && suggestedTopics.length > 0 && (
					<Box
						xcss={BadgeStyles}
						as="span"
						data-testId="navigation-apps-sidebar-servicedesk.common.ui.knowledge-item.hub-menu.suggested-topics-badge"
					>
						<Badge>{suggestedTopics.length}</Badge>
					</Box>
				)}
			</MenuItem>
		);
	}, [basePath, checkIsTopicsFiltered, error, formatMessage, loading, suggestedTopics.length]);

	const renderCategoriesList = useCallback(() => {
		return (
			<Section title={formatMessage(messages.categories)} hasSeparator>
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="categories">
						{({ innerRef: droppableRef, droppableProps, placeholder }) => (
							<div ref={droppableRef} {...droppableProps}>
								{categories.map((category, index) => (
									<Draggable
										disableInteractiveElementBlocking
										key={String(category.id)}
										draggableId={String(category.id)}
										index={index}
									>
										{({ innerRef, dragHandleProps, draggableProps }) => (
											<MenuItem
												analytics={{
													itemId: `category-${String(category.id)}`,
												}}
												href={`${basePath}/categories/${String(category.id)}`}
												ref={innerRef}
												selectedOn={matchUrlPathname()}
												{...{
													...dragHandleProps,
													...draggableProps,
												}}
											>
												{category.name}
											</MenuItem>
										)}
									</Draggable>
								))}
								{placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</Section>
		);
	}, [basePath, categories, formatMessage, handleDragEnd]);

	const renderNewCategory = useCallback(() => {
		return (
			<Section hasSeparator>
				<MenuItem
					analytics={{ itemId: 'new-category' }}
					iconBefore={<AddIcon spacing="spacious" label="" />}
					onClick={() => openCreateCategory(true)}
				>
					{formatMessage(messages.newCategory)}
				</MenuItem>
			</Section>
		);
	}, [formatMessage, openCreateCategory]);

	const allowedSourceTypes = ['NATIVE_CONFLUENCE', 'SERVER_CONFLUENCE'];
	const isDraftsAndCategoriesAllowed = () => {
		if (fg('kb-cross-site-enabled') || fg('3p-integration-enabled_t5ysh')) {
			if (sourceType && allowedSourceTypes.includes(sourceType)) {
				return true;
			}
			return false;
		}
		return true;
	};

	return (
		<>
			<Section>
				<TitleHeader>{formatMessage(messages.heading)}</TitleHeader>
			</Section>

			{isDraftsAndCategoriesAllowed() ? (
				<>
					<Section>
						{renderArticles()}
						{fg('jsm-suggested-topics-reorder') ? (
							<>
								{showSuggestedTopics && renderSuggestedTopics()}
								{renderDrafts()}
							</>
						) : (
							<>
								{renderDrafts()}
								{showSuggestedTopics && renderSuggestedTopics()}
							</>
						)}
					</Section>
					{isCategoriesSupported && categories.length > 0 ? renderCategoriesList() : null}
					{isCategoriesSupported ? renderNewCategory() : null}
				</>
			) : (
				<Section>
					{renderArticles()}
					{showSuggestedTopics && renderSuggestedTopics()}
				</Section>
			)}
		</>
	);
};
const BadgeStyles = xcss({
	marginLeft: 'space.150',
});
